import {cn} from '@tempmee/core'
import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  forwardRef,
  useId,
  useState,
} from 'react'
import {
  textFieldHelperClass,
  textFieldIconWrapperClass,
  textFieldVariants,
} from './TextFieldVariants'

/** The element a `TextField` renders — an `<input>`, or a `<textarea>` when `multiline`. */
type FieldElement = HTMLInputElement | HTMLTextAreaElement

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Floating label rendered inside the field; doubles as the placeholder when empty */
  label?: string
  /** Helper or validation message rendered below the field */
  helperText?: ReactNode
  /** Applies error styling (pink fill + red underline) and colours the helper text */
  error?: boolean
  /** Icon rendered on the left side of the field */
  leadingIcon?: ReactNode
  /** Icon rendered on the right side of the field */
  trailingIcon?: ReactNode
  /**
   * Renders a `<textarea>` instead of an `<input>`, keeping the filled fill and
   * the embedded floating label. Mirrors MUI's `multiline`.
   * @default false
   */
  multiline?: boolean
  /** Visible text rows when `multiline` — sets the field's initial height. @default 4 */
  rows?: number
}

/**
 * The embedded label that floats up once the field is focused or holds a value.
 * Exported for sibling filled-field components (e.g. `Autocomplete`) to reuse the
 * exact float behaviour — intentionally NOT re-exported from the package barrel.
 */
export function FloatingLabel({
  htmlFor,
  shrink,
  disabled,
  multiline,
  children,
}: {
  htmlFor: string
  shrink: boolean
  disabled?: boolean
  multiline?: boolean
  children: ReactNode
}) {
  const color = disabled
    ? 'text-text-disabled'
    : shrink
      ? 'text-text-primary'
      : 'text-text-secondary'
  // Resting, the label sits over the field's first line. On a single-line field
  // that line *is* the box, so centring it works; on a multiline one the box is
  // several lines tall, so it pins to the same 8px offset explicitly.
  const resting = multiline ? 'top-2' : '-translate-y-1/2 top-1/2'
  const position = shrink
    ? 'top-0 text-[12px] font-normal leading-4'
    : `${resting} text-base font-semibold leading-6`

  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'pointer-events-none absolute left-0 origin-left tracking-[0.15px] transition-all duration-150 ease-out',
        position,
        color,
      )}
    >
      {children}
    </label>
  )
}

function FieldIcon({node, disabled}: {node: ReactNode; disabled?: boolean}) {
  return (
    <span className={cn(textFieldIconWrapperClass, disabled && 'text-text-disabled')}>{node}</span>
  )
}

/**
 * MUI-style filled text field with an embedded floating label.
 *
 * The label sits centred as a placeholder when the field is empty and unfocused,
 * then shrinks and floats to the top once the field is focused or holds a value.
 * For a bare input box without an embedded label, use `InputTextField`.
 *
 * Pass `multiline` for the Figma `TextField/MultiLine` treatment — the same
 * filled box over a `<textarea>` sized by `rows`. The forwarded ref then points
 * at that `<textarea>` (its props type stays `HTMLInputElement` so existing
 * single-line callers keep compiling).
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      className,
      label,
      helperText,
      error = false,
      leadingIcon,
      trailingIcon,
      multiline = false,
      rows = 4,
      disabled,
      id,
      value,
      defaultValue,
      placeholder,
      onFocus,
      onBlur,
      onChange,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId()
    const inputId = id ?? generatedId
    const helperId = `${inputId}-helper`

    const isControlled = value !== undefined
    const [focused, setFocused] = useState(false)
    const [uncontrolledFilled, setUncontrolledFilled] = useState(
      () => defaultValue != null && String(defaultValue).length > 0,
    )
    const filled = isControlled ? value != null && String(value).length > 0 : uncontrolledFilled

    // The label floats up once the field is focused or holds a value.
    const shrink = focused || filled
    const helperColor = disabled
      ? 'text-text-disabled'
      : error
        ? 'text-error'
        : 'text-text-secondary'

    // Both branches share these; only the element and its sizing differ. The
    // handlers take the union so they can be spread onto either element, then
    // narrow back to the props' `HTMLInputElement` signature to call out.
    const fieldProps = {
      id: inputId,
      disabled,
      value,
      defaultValue,
      placeholder: !label || shrink ? placeholder : undefined,
      'aria-invalid': error || undefined,
      'aria-describedby': helperText ? helperId : undefined,
      onFocus: (e: FocusEvent<FieldElement>) => {
        setFocused(true)
        onFocus?.(e as FocusEvent<HTMLInputElement>)
      },
      onBlur: (e: FocusEvent<FieldElement>) => {
        setFocused(false)
        onBlur?.(e as FocusEvent<HTMLInputElement>)
      },
      onChange: (e: ChangeEvent<FieldElement>) => {
        if (!isControlled) setUncontrolledFilled(e.target.value.length > 0)
        onChange?.(e as ChangeEvent<HTMLInputElement>)
      },
    }
    const fieldClass = cn(
      'w-full bg-transparent text-base font-semibold leading-6 outline-none placeholder:text-text-disabled',
      disabled ? 'text-text-disabled' : 'text-text-primary',
    )

    return (
      <div className={cn('flex w-full flex-col gap-2', className)}>
        <div className={textFieldVariants({isError: error, disabled, multiline})}>
          {leadingIcon && <FieldIcon node={leadingIcon} disabled={disabled} />}

          <div className={cn('relative flex flex-1', multiline ? 'flex-col' : 'h-10 items-center')}>
            {label && (
              <FloatingLabel
                htmlFor={inputId}
                shrink={shrink}
                disabled={disabled}
                multiline={multiline}
              >
                {label}
              </FloatingLabel>
            )}
            {multiline ? (
              <textarea
                // The public props/ref type stays `HTMLInputElement` so the
                // single-line callers that predate `multiline` keep compiling;
                // at runtime the ref is this textarea.
                ref={ref as unknown as Ref<HTMLTextAreaElement>}
                rows={rows}
                className={cn(
                  fieldClass,
                  'ds-scroll-area resize-none',
                  // Reserve the floated label's row above the text.
                  label && 'pt-4',
                )}
                {...fieldProps}
                {...(props as object)}
              />
            ) : (
              <input
                ref={ref}
                className={cn(fieldClass, label && 'absolute inset-0 h-full pt-4')}
                {...fieldProps}
                {...props}
              />
            )}
          </div>

          {trailingIcon && <FieldIcon node={trailingIcon} disabled={disabled} />}
        </div>

        {helperText && (
          <p id={helperId} className={cn(textFieldHelperClass, helperColor)}>
            {helperText}
          </p>
        )}
      </div>
    )
  },
)

TextField.displayName = 'TextField'
